import { useRef, useState } from 'react';
import { UploadCloud, X, File as FileIcon } from 'lucide-react';
import type { FileDropProps } from './FileDrop.types';
import './FileDrop.scss';

const formatSize = (b: number) => {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};

export default function FileDrop({
  items, onAdd, onRemove, accept, multiple = false, maxSize, disabled, label, hint, className,
}: FileDropProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);

  const filter = (files: File[]) => {
    let arr = files;
    if (maxSize) arr = arr.filter(f => f.size <= maxSize);
    if (!multiple) arr = arr.slice(0, 1);
    if (arr.length) onAdd(arr);
  };

  return (
    <div className={['file-drop', disabled ? 'file-drop--disabled' : '', className || ''].filter(Boolean).join(' ')}>
      {label ? <span className="file-drop__label">{label}</span> : null}
      <div
        className={['file-drop__zone', over ? 'file-drop__zone--over' : ''].filter(Boolean).join(' ')}
        onDragOver={e => { e.preventDefault(); if (!disabled) setOver(true); }}
        onDragLeave={() => setOver(false)}
        onDrop={e => { e.preventDefault(); setOver(false); if (disabled) return; filter(Array.from(e.dataTransfer.files)); }}
        onClick={() => !disabled && inputRef.current?.click()}
        role="button"
        tabIndex={0}
        onKeyDown={e => { if ((e.key === 'Enter' || e.key === ' ') && !disabled) { e.preventDefault(); inputRef.current?.click(); } }}
      >
        <span className="file-drop__icon" aria-hidden="true"><UploadCloud size={28} /></span>
        <span className="file-drop__primary">Перетащите файлы или нажмите для выбора</span>
        {hint ? <span className="file-drop__hint">{hint}</span> : null}
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          disabled={disabled}
          className="file-drop__native"
          onChange={e => filter(Array.from(e.target.files ?? []))}
        />
      </div>
      {items.length > 0 ? (
        <ul className="file-drop__list">
          {items.map(it => (
            <li key={it.id} className="file-drop__item">
              <span className="file-drop__item-icon" aria-hidden="true"><FileIcon size={16} /></span>
              <span className="file-drop__item-info">
                <span className="file-drop__item-name">{it.file.name}</span>
                <span className="file-drop__item-meta">{formatSize(it.file.size)}{it.error ? ` • ${it.error}` : ''}</span>
                {typeof it.progress === 'number' && it.progress < 100 ? (
                  <span className="file-drop__progress"><span className="file-drop__progress-bar" style={{ width: `${it.progress}%` }} /></span>
                ) : null}
              </span>
              <button type="button" aria-label="Удалить файл" className="file-drop__remove" onClick={() => onRemove(it.id)}>
                <X size={14} />
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
